import classNames from 'classnames';
import React, { useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { getEmptyImage } from 'react-dnd-html5-backend';
import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import AssetActions from 'components/AssetActions';
import AssetsCollectionEntities from 'components/AssetsCollectionEntities';
import DocumentThumbnail from 'components/DocumentThumbnail';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import * as Models from 'models';
import styles from './styles.module.scss';
import { useAssetsCollectionScreenDragSource } from './useAssetsCollectionScreenDragSource';

type Props = {
  document: Models.AssetsCollectionMap;
  screen: Models.ScreenMap;
  screenOrder: number;
  showDetails(previewUrl: string): void;
  dataTestId?: string;
};

const AssetsCollectionScreen: React.FC<Props> = (props) => {
  const {
    document,
    screen,
    screenOrder,
    showDetails,
    dataTestId = 'assetCollectionScreen',
  } = props;

  const { isDragging, draggable, connectDragPreview, connectDragSource } = useAssetsCollectionScreenDragSource({
    document,
    screen,
    screenOrder,
  });

  const [expanded, setExpanded] = useState(false);
  const toggleExpanding = (): void => setExpanded(!expanded);

  const name = screen.get('name');
  const thumbnailUrl = screen.getIn(['thumbnail', 'url']);
  const onPreviewOpen = (): void => showDetails(thumbnailUrl);

  React.useEffect(
    () => { connectDragPreview(getEmptyImage(), { captureDraggingState: true }); },
    [],
  );

  return connectDragSource((
    <div className={classNames(styles.AssetsCollectionScreen, { [styles.draggable]: draggable, [styles.isDragging]: isDragging })}
      data-testid={`${dataTestId}-container`}>
      <div className={styles.contentInvisible}>
        <div className={styles.screen} data-testid={`${dataTestId}-container-screen`}>
          <div className={styles.preview} data-testid={`${dataTestId}-container-screen-preview`}>
            <DocumentThumbnail
              placeholder={<Icon type={IconType.IMAGE} size="sm-md" color="grey" />}
              url={thumbnailUrl}
              dataTestId={`${dataTestId}-container-screen-preview-documentThumbnail`}
            />
            {thumbnailUrl && <AssetActions onPreviewOpen={onPreviewOpen} dataTestId={`${dataTestId}-container-screen-preview-assetActions`} />}
          </div>
          <AccessibleDiv
            className={styles.toggleArea}
            onClick={toggleExpanding}
            data-testid={`${dataTestId}-container-screen-button`}
          >
            <div className={styles.info} data-testid={`${dataTestId}-container-screen-button-name`}>{name}</div>
            <div className={styles.chevron} data-testid={`${dataTestId}-container-screen-button-chevron`}>
              <Icon
                type={expanded ? IconType.CHEVRON_UP : IconType.CHEVRON_DOWN}
                size="badge"
                color="primary"
              />
            </div>
          </AccessibleDiv>
        </div>
        <AnimateHeight
          duration={300}
          easing="ease-out"
          height={expanded ? 'auto' : 0}
        >
          <AssetsCollectionEntities document={document} screenOrder={screenOrder} screen={screen} dataTestId={`${dataTestId}-container-entities`} />
        </AnimateHeight>
      </div>
    </div>
  ));
};

export default AssetsCollectionScreen;
